import React, { useEffect, useState, useMemo } from 'react';
import { Box, Typography, Button, Divider, Modal, IconButton, Stack, CircularProgress, Alert } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import RemoveIcon from "@mui/icons-material/Remove";
import AddIcon from "@mui/icons-material/Add";
import axios from 'axios';
import { notificationService } from '../../service/FCM/NotificationService';
import { createBilledInvoice, viewOrderDetails } from '../../service/order/orderService';
import { notifyError, notifySuccess } from '../ToastContainer/Toast/Toast';
import VegIcon from '../../Assets/Veg_symbol.svg.png';
import NonVegIcon from '../../Assets/Non_veg_symbol.svg.png';
import EggIcon from '../../Assets/Egg.svg';

const modalStyle = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 400,
  bgcolor: 'background.paper',
  borderRadius: 2,
  boxShadow: 24,
  p: 0,
  overflow: 'hidden',
};

interface OrderItem {
  id: number;
  product_id: string;
  product_name: string;
  quantity: number;
  price: string;
  original_price?: string;
  discount_type?: string;
  discount_value?: string;
  gst_percentage?: string;
  gst_price?: string;
  total_price?: string;
  product_type?: string;
  isVeg?: string;
}

interface OrderData {
  id: number;
  order_code: string;
  order_date: string;
  status: string;
  preparation_time?: string;
  customer: {
    id: number;
    name: string;
    email: string;
    phone: string;
  };
  order_items: OrderItem[];
}

interface AcceptOrderModalProps {
  open: boolean;
  onClose: () => void;
  orderData: OrderData | null;
  onAcceptSuccess: () => void;
}

const parsePreparationTimeToMinutes = (value?: string): number => {
  if (!value || typeof value !== "string") return 10;
  const match = value.trim().toUpperCase().match(/^PT(?:(\d+)H)?(?:(\d+)M)?$/);
  if (!match) return 10;

  const hours = Number(match[1] || 0);
  const minutes = Number(match[2] || 0);
  const totalMinutes = hours * 60 + minutes;

  return Number.isFinite(totalMinutes) && totalMinutes >= 0 ? totalMinutes : 10;
};

const getGroupedDisplayItems = (items: OrderItem[]) => {
  // Add null/undefined check
  if (!items || !Array.isArray(items)) {
    console.warn('getGroupedDisplayItems: items is not a valid array:', items);
    return [];
  }

  const baseProductsMap = new Map<string, OrderItem & { addons?: OrderItem[] }>();
  const processedAddonIds = new Set<number>(); // Track addons already nested

  items.forEach(item => {
    const itemProductIdStr = String(item.product_id); // Ensure product_id is string for startsWith/match

    if (itemProductIdStr.startsWith('a-')) {
      // This is an addon
      // Regex to find the base product ID from an addon ID like 'a-123-456' or 'a-123'
      const baseProductIdMatch = itemProductIdStr.match(/a-(\d+)(?:-\d+)?/);
      if (baseProductIdMatch && baseProductIdMatch[1]) {
        const baseProductId = baseProductIdMatch[1];
        // Try to get the base product from the map first, or find it in the original list
        let foundBaseProduct = baseProductsMap.get(baseProductId);

        if (!foundBaseProduct) {
          // If not in map, try to find the actual base product in the current Order's items
          const originalBaseItem = items.find(bp => !String(bp.product_id).startsWith('a-') && String(bp.product_id) === baseProductId);
          if (originalBaseItem) {
            foundBaseProduct = { ...originalBaseItem, addons: [] };
            baseProductsMap.set(baseProductId, foundBaseProduct);
          }
        }

        if (foundBaseProduct) {
          if (!foundBaseProduct.addons) {
            foundBaseProduct.addons = [];
          }
          foundBaseProduct.addons.push(item);
          processedAddonIds.add(item.id); // Mark this addon as processed
        } else {
          // If no base product is found, treat this addon as a standalone item
          // Use a unique key for standalone addons to avoid overwriting base products
          baseProductsMap.set(`standalone-addon-${item.id}`, { ...item, addons: [] });
          processedAddonIds.add(item.id);
        }
      } else {
        // If addon product_id doesn't match expected pattern, treat as standalone
        baseProductsMap.set(`standalone-addon-${item.id}`, { ...item, addons: [] });
        processedAddonIds.add(item.id);
      }
    } else {
      // This is a base product
      if (!baseProductsMap.has(itemProductIdStr)) {
        baseProductsMap.set(itemProductIdStr, { ...item, addons: [] });
      } else {
        // If it already exists (e.g., an addon might have created an entry), merge
        const existing = baseProductsMap.get(itemProductIdStr);
        baseProductsMap.set(itemProductIdStr, { ...item, addons: existing?.addons || [] });
      }
    }
  });

  // Add any remaining items that were not processed (e.g., standalone items that are not addons)
  items.forEach(item => {
    const itemProductIdStr = String(item.product_id);
    // Check if it's a base product not yet added, or an addon not yet processed
    if ((!itemProductIdStr.startsWith('a-') && !baseProductsMap.has(itemProductIdStr)) ||
      (itemProductIdStr.startsWith('a-') && !processedAddonIds.has(item.id) && !baseProductsMap.has(`standalone-addon-${item.id}`))) {
      baseProductsMap.set(`unprocessed-item-${item.id}`, { ...item, addons: [] });
    }
  });

  return Array.from(baseProductsMap.values());
};

const AcceptOrderModal: React.FC<AcceptOrderModalProps> = ({
  open,
  onClose,
  orderData,
  onAcceptSuccess,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [prepTime, setPrepTime] = useState(10);

  const handleIncrement = () => setPrepTime(prev => Math.min(prev + 5, 60));
  const handleDecrement = () => setPrepTime(prev => Math.max(prev - 5, 0));

  // Reset prepTime when modal opens
  useEffect(() => {
    if (open) {
      setPrepTime(parsePreparationTimeToMinutes(orderData?.preparation_time));
    }
  }, [open, orderData?.preparation_time]);

  const groupedDisplayItems = useMemo(() => {
    return orderData && orderData.order_items ? getGroupedDisplayItems(orderData.order_items) : [];
  }, [orderData]);

  const handleProcessOrder = async () => {
    if (!orderData) {
      setError("No order data available to process.");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // If order_items is missing (FCM fallback data), fetch full order details first
      let finalOrderData = orderData;
      if (!orderData.order_items || orderData.order_items.length === 0) {
        const orderId = orderData.id || (orderData as any).orderId;
        console.log('[AcceptOrderModal] order_items missing, fetching full order details for:', orderId);
        const res = await viewOrderDetails(orderId, false);
        const data = res?.data?.data;
        if (data && data.order_items) {
          finalOrderData = data;
        } else {
          notifyError("Could not load order details. Please try again.");
          setLoading(false);
          return;
        }
      }

      const payload = {
        order_id: finalOrderData.id,
        items: finalOrderData.order_items.map((item) => ({
          item_id: item.id,
          product_id: item.product_id,
          quantity: item.quantity,
        })),
        cancel_entire_order: false,
        type: "bill",
        preparation_time: `PT${prepTime}M`
      };

      const response = await createBilledInvoice(payload);

      if (response?.status === 201) {
        notifySuccess(`Order ${finalOrderData.id} accepted and processed successfully!`);
        notificationService.acceptOrder(finalOrderData.id.toString());
        onAcceptSuccess();
        onClose();
      } else {
        notifyError(`Failed to process order: ${response?.status}`);
      }
    } catch (error) {
      // When the API is in-flight and navigation happens (e.g. FCM confirms order status changed),
      // abortInFlightRequests() cancels the response. The order WAS processed — show success.
      if (axios.isCancel(error)) {
        console.log('[AcceptOrderModal] Request cancelled due to navigation — order processed successfully');
        notifySuccess('Order accepted and processed successfully!');
        onClose();
        return;
      }
      notifyError("Failed to process the order. Please try again.");
      onClose();
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose}>
      <Box sx={modalStyle}>
        {/* Header Section */}
        <Box sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          bgcolor: 'transparent',
          p: 3,
          pb: 2,
        }}>
          <Box>
            <Typography variant="h6" fontWeight="bold" sx={{ fontSize: '1.2rem' }}>Accept Order</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
              {orderData?.id} | {orderData?.order_date ? new Date(orderData.order_date).toLocaleTimeString() : ''}
            </Typography>
          </Box>
          <IconButton onClick={onClose} sx={{ p: 0.5 }}>
            <CloseIcon sx={{ fontSize: '1.2rem', border: '1px solid transparent' }} />
          </IconButton>
        </Box>

        {/* First Divider */}
        <Divider sx={{ my: 0, mx: 0, bgcolor: '#e0e0e0' }} />

        {/* Order Items Section */}
        <Stack sx={{ py: 2, px: 3, maxHeight: '300px', overflowY: 'auto' }}>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          
          {groupedDisplayItems.length > 0 ? (
            groupedDisplayItems.map((item, index) => (
              <Box
                key={item.product_id || index} 
                display="flex"
                flexDirection="column" 
                alignItems="flex-start"
                mb={1} 
              >
                {/* Main Product Name + Qty and Price */}
                <Box display="flex" justifyContent="space-between" width="100%">
                  <Box display="flex" alignItems="center" gap={1}>
                    {item.product_type === "veg" && (
                      <img src={VegIcon} alt="Veg" style={{ width: 16, height: 16 }} />
                    )}
                    {item.product_type === "non-veg" && (
                      <img src={NonVegIcon} alt="Non-Veg" style={{ width: 16, height: 16 }} />
                    )}
                    {item.product_type === "egg" && (
                      <img src={EggIcon} alt="Egg" style={{ width: 16, height: 16 }} />
                    )}
                    <Typography sx={{ fontSize: "0.9rem", fontWeight: 600 }}>
                      {item.product_name} x {item.quantity}
                    </Typography>
                  </Box>
                  {/* Right: Price */}
                  <Typography fontWeight={500}>
                    ₹{item.total_price || item.price}
                  </Typography>
                </Box>

                {/* Addons Section */}
                {item.addons && item.addons.length > 0 && (
                  <Box sx={{ pl: 3, width: '100%' }}>
                    {item.addons.map((addon, j) => (
                      <Box key={addon.id || j} display="flex" justifyContent="space-between" width="100%">
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ display: "block", pr: 1 }}
                        >
                          • {addon.product_name} x {addon.quantity}
                        </Typography>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ display: "block" }}
                        >
                          ₹{addon.total_price || addon.price}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                )}
              </Box>
            ))
          ) : (
            <Typography variant="caption" color="text.secondary">
              No items available
            </Typography>
          )}
        </Stack>

        <Divider sx={{ my: 0, mx: 0, bgcolor: '#e0e0e0' }} />

        {/* Set Preparation Time Section */}
        <Box sx={{ 
          px: 3, 
          py: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between"
        }}>
          <Typography fontWeight={600} sx={{ fontSize: "0.95rem" }}>
            Set Preparation Time
          </Typography>
          <Box sx={{ 
            display: "flex", 
            alignItems: "center",
            gap: 1.5,
            border: "1px solid rgb(170, 169, 169)",
            borderRadius: 1,
          }}>
            <IconButton 
              onClick={handleDecrement}
              disabled={prepTime <= 0 || loading}
              size="small"
              sx={{ 
                minWidth: 32,
                width: 32,
                height: 32,
                padding: 0,
                color: "black",
                border: '1px solid transparent',
                '&:hover': { bgcolor: 'transparent', border: '1px solid transparent' },
                '&:disabled': { opacity: 0.5 }
              }}
            >
              <RemoveIcon sx={{ fontSize: '1.2rem' }} />
            </IconButton>
            <Typography 
              sx={{ 
                fontSize: "1rem", 
                fontWeight: 600,
                color: "#1976d2",
                minWidth: 50,
                textAlign: "center"
              }}
            >
              {prepTime} Min
            </Typography>
            <IconButton 
              onClick={handleIncrement}
              disabled={prepTime >= 60 || loading}
              size="small"
              sx={{ 
                minWidth: 32,
                width: 32,
                height: 32,
                padding: 0,
                color: "black",
                border: '1px solid transparent',
                '&:hover': { bgcolor: 'transparent', border: '1px solid transparent' },
                '&:disabled': { opacity: 0.5 }
              }}
            >
              <AddIcon sx={{ fontSize: '1.2rem' }} />
            </IconButton>
          </Box>
        </Box>

        <Divider sx={{ my: 0, mx: 0, bgcolor: '#e0e0e0' }} />

        <Button
          fullWidth
          variant="contained"
          onClick={handleProcessOrder} 
          disabled={loading || !orderData} 
          sx={{
            bgcolor: 'black',
            textTransform: 'none',
            py: 1.5,
            borderRadius: 1,
            mb: 3, mt: 3,
            mx: 3,
            width: 'calc(100% - 48px)',
            fontSize: '1rem',
            fontWeight: 'bold',
            '&:hover': { bgcolor: '#333' }
          }}>
          {loading ? <CircularProgress size={24} color="inherit" /> : 'Process'} 
        </Button>
      </Box>
    </Modal>
  );
};

export default AcceptOrderModal;
